/**
 * event-header.js
 */

import React from 'react';
import { Button } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link as RouterLink } from 'react-router-dom';
import { parseISO } from 'date-fns';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import useRecordStore from 'contexts/record-store';
import { UserContext } from 'contexts/user/user-context';
import RecordMenuContext from 'contexts/record-context-menu/record-menu.context';
import RouteLink from 'routing/route-link';
import Icon from 'components/icon';
import { AttendanceControl, AttendanceIcon } from 'components/event-attendance/event-attendance';
import Row from '../row/row';
import RowLocationDate from '../row/row-location-date';
import { shrinkOptionalWidth } from '../helpers';
import { useTranslation } from 'react-i18next';


const useStyles = makeStyles(theme => ({
  root: {
    height: 40,
    transition: `${theme.transitions.duration.shorter}ms ${theme.transitions.easing.easeOut}`,
    backgroundColor: theme.palette.comp.recordRow.event.background,
    '&:hover': {
      backgroundColor: theme.palette.comp.recordRow.headerHover(theme.palette.comp.recordRow.event.background)
    },
    '&.not-attending': {
      backgroundColor: theme.palette.comp.recordRow.event.backgroundNotAttending,
      '&:hover': {
        backgroundColor: theme.palette.comp.recordRow.headerHover(theme.palette.comp.recordRow.event.backgroundNotAttending)
      },
    }
  },
  icon: {
    color: 'inherit',
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      '&.MuiSvgIcon-root.undecided': {
        color: theme.palette.secondary.colors[6],
      }
    }
  },
  attendance: {
    flex: 1,
    textAlign: 'center',
    position: 'relative',
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      '& .attendance-control': {
        display: 'none'
      },
    }
  },
  addExercise: {
    flex: 1,
    whiteSpace: 'nowrap',
    textAlign: props => props.user.role.isTrainer ? 'right' : 'center',
    '& .MuiButton-root': {
      color: 'inherit',
      padding: `1px 9px`,
      transition: '150ms ease-in-out',
      fontWeight: 400,
      '&:hover': {
        color: theme.palette.primary.colors[0],
        backgroundColor: theme.palette.secondary.colors[5],
      }
    },
    [theme.breakpoints.down(shrinkOptionalWidth)]: {
      display: 'none'
    }
  },
}));


const EventIcon = (props) => {
  if (!props.isOptional) {
    return <Icon type='event' />;
  } else {
    return <AttendanceIcon {...props} />;
  }
}


const EventHeader = React.memo(function (props) {
  const {
    id,
    basePath,
    isRecordTrackingRelevant,
  } = props;
  const { user } = React.useContext(UserContext);
  const classes = useStyles({ user });
  const { t } = useTranslation('general');
  const recordStore = useRecordStore();
  const recordContextMenu = React.useContext(RecordMenuContext);
  const eventRecord = recordStore.useRecord(Types.Record.EVENT, id).ornull;

  if (!eventRecord) return null;

  const event = eventRecord.get();
  const eventDate = parseISO(event.date);
  const notAttending = event.membership?.isOptional && event.membership?.isAttending === false;
  const canAddExercise = (user.role.isHandler && Boolean(event.membership?.isLeader)) || user.role.isTrainer;
  const customCompare = `irtr:${isRecordTrackingRelevant}`;

  const to = makeLocation(basePath, 'training', 'viewEvent', { eventId: id });
  const routeLink = <RouterLink to={to} className='absolute-fill record-link' />;


  const menuItems = [
    // view
    {
      title: 'View',
      iconType: 'view',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'viewEvent', { eventId: id }, null),
      isDefaultItem: true,
    },

    // edit
    !user.role.isSupervisor ? {
      title: 'Edit',
      iconType: 'edit',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'editEvent', { eventId: id }, null),
      disabled: !user.role.status.isActive || !event.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to edit this event'
    } : {},

    // + exer
    canAddExercise ? {
      title: 'Add Exercise',
      iconType: 'add',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'addExercise', { eventId: id }, null),
      disabled: !user.role.status.isActive || !event.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to edit this event'
    } : {},

    // attendance
    event.membership?.isOptional && !user.role.isSupervisor
      ? {
        title: 'Attendance',
        iconType: 'attendance',
        disabled: !user.role.status.isActive,
        disabledReason: t('readOnlyAccount'),
        submenuItems: [
          {
            title: 'Attending',
            iconType: 'attending',
            onSelect: () => recordStore.setAttendance(true, id),
            disabled: !user.role.status.isActive,
            disabledReason: t('readOnlyAccount'),
          }, {
            title: 'Not Attending',
            iconType: 'not-attending',
            onSelect: () => recordStore.setAttendance(false, id),
            disabled: !user.role.status.isActive,
            disabledReason: t('readOnlyAccount'),
          }
        ]
      }
      : {},

    // duplicate
    !user.role.isSupervisor ? {
      title: 'Duplicate ...',
      iconType: 'copy',
      onSelect: () => recordContextMenu.duplicateEvent(id),
      disabled: !user.role.status.isActive,
      disabledReason: t('readOnlyAccount'),
    } : {},

    // delete
    !user.role.isSupervisor ? {
      title: 'Delete ...',
      iconType: 'delete',
      onSelect: () => recordContextMenu.deleteEvent(id),
      disabled: !user.role.status.isActive || !event.canEdit || event.hasCompletions || isRecordTrackingRelevant,
      disabledReason: !user.role.status.isActive 
        ? t('readOnlyAccount')
        : !event.canEdit
          ? 'You do not have permission to delete the containing event'
          : event.hasCompletions
            ? 'Events with completions cannot be deleted'
            : isRecordTrackingRelevant
              ? 'Tracking is active for this event'
              : ''
    } : {}
  ];


  return (
    <Row
      data-testid={id}
      data-testtype='header-event'
      recordType={Types.Record.EVENT}
      isHeader
      className={`header ${classes.root} ${notAttending ? 'not-attending' : ''}`}
      routeLink={routeLink}
      icon={<EventIcon
        isOptional={event.membership?.isOptional}
        isAttending={event.membership?.isAttending}
        className={classes.icon} />}
      textVariant='body2'
      title={event.name}

      detail={<RowLocationDate
        location={event.location.locationName || event.location.address1 || 'no address'}
        date={eventDate}
      />}

      optional={<>
        <div className='flex-hc'>
          {user.role.isHandler &&
          <>
            {/* ATTENDANCE */}
            <div className={classes.attendance}>
              {/* disabled control with functional link when has completions */}
              {(event.membership?.isOptional && event.membership?.hasCompletions) &&
              <RouterLink to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
                <AttendanceControl 
                  eventRecord={eventRecord}
                  disabled={true}
                  tooltip="You cannot change attendance when you have completed records from this event."
                />
              </RouterLink>}

              {/* enabled control when no completions */}
              {!(event.membership?.isOptional && event.membership?.hasCompletions) &&
              <AttendanceControl 
                eventRecord={eventRecord}
                disabled={!user.role.status.isActive}
              />}
            </div>
          </>}

          {canAddExercise &&
          <>
            {/* ADD EXERCISE */}
            <div className={classes.addExercise}>
              <Button
                size='small'
                component={RouteLink}
                basePath={basePath}
                pathSectionName='training'
                pathItemName='addExercise'
                pathParams={{ eventId: id }}
                disabled={!user.role.status.isActive}
                data-testid='add-exercise-event-row'
              >
                + Exercise
              </Button>
            </div>
          </>}
        </div>
      </>}

      menu={{
        ariaLabel: 'event menu options',
        items: menuItems
      }}

      customCompare={customCompare}
    />
  )
}, (prev, next) => {
  return (
    prev.id === next.id
    && prev.isRecordTrackingRelevant === next.isRecordTrackingRelevant
  );
});

export default EventHeader;